import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, MapPin, Pencil, CheckCircle2, X, FolderKanban, ClipboardList,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { MedicalLookup, medicalFromSite, medicalToPayload, isRemoteKm, type MedicalValues } from "./MedicalLookup";
import { Button, Card, Field, Input, Select, Textarea, Pill, Spinner, EmptyState, type Tone } from "@/components/ui";

// Map free-form status strings to the Pill Tone union (best-effort; fallback default).
function projectStatusTone(s: string): Tone {
  const valid: Tone[] = ["live", "mobilising", "acceptance_pending", "on_hold", "closed", "cancelled"];
  return (valid as string[]).includes(s) ? (s as Tone) : "default";
}
function bidStatusTone(s: string): Tone {
  if (s === "won") return "won";
  if (s === "lost") return "lost";
  if (s === "open" || s === "responded") return "open";
  return "default";
}

// =============================================================================
// SiteDetailScreen — /sites/:siteId
// View + inline edit + linked projects + linked bids. Manager+ can edit and
// toggle active status. RLS also enforces. T-037.
// =============================================================================

// Telco-relevant types per James 2026-04-26 (mirrors NewSiteScreen).
const SITE_TYPES = [
  "Lattice Tower",
  "Monopole",
  "Guyed Mast",
  "Rooftop",
  "Watertank",
  "Other",
] as const;
const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"] as const;
const MOBILE_COVERAGE = ["good", "marginal", "none"] as const;

interface SiteFull {
  site_id:          string;
  site_code:        string;
  site_name:        string;
  rfnsa_id:         string | null;
  carrier_site_id:  string | null;
  site_type:        string | null;
  structure_height_m: number | null;
  asset_owner_id:   string | null;
  address_line1:    string | null;
  address_line2:    string | null;
  suburb:           string | null;
  state:            string | null;
  postcode:         string | null;
  latitude:         number | null;
  longitude:        number | null;
  what3words:       string | null;
  land_owner_name:  string | null;
  access_details:   string | null;
  access_restrictions: string | null;
  parking_notes:    string | null;
  key_location:     string | null;
  hazards_summary:  string | null;
  nearest_hospital_name:         string | null;
  nearest_hospital_address:      string | null;
  nearest_hospital_phone:        string | null;
  nearest_hospital_distance_km:  number | null;
  nearest_medical_centre_name:        string | null;   // mig 395
  nearest_medical_centre_address:     string | null;
  nearest_medical_centre_phone:       string | null;
  nearest_medical_centre_distance_km: number | null;
  mobile_coverage:  string | null;
  notes:            string | null;
  is_active:        boolean;
  created_at:       string;
  updated_at:       string;
  asset_owner?:     { owner_code: string; display_name: string } | null;
}

interface LinkedProject {
  project_id:   string;
  project_code: string;
  project_name: string;
  status:       string | null;
  scheduled_start_date: string | null;
  scheduled_end_date:   string | null;
}

interface LinkedBid {
  bid_id:     string;
  bid_number: string;
  bid_title:  string;
  status:     string;
  due_date:   string | null;
  estimated_value: number | null;
}

export default function SiteDetailScreen() {
  const { siteId } = useParams<{ siteId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canEdit = user?.role === "director" || user?.role === "manager";
  const [editMode, setEditMode] = useState(false);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------
  const { data: site, isLoading, error } = useQuery({
    queryKey: ["site_detail", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      // (supabase as any) — nearest_medical_centre_* (mig 395) not in database.types.ts yet
      const { data, error } = await (supabase as any)
        .from("sites")
        .select(`
          site_id, site_code, site_name, rfnsa_id, carrier_site_id, site_type,
          structure_height_m, asset_owner_id,
          address_line1, address_line2, suburb, state, postcode, latitude, longitude, what3words,
          land_owner_name, access_details, access_restrictions, parking_notes,
          key_location, hazards_summary, nearest_hospital_name,
          nearest_hospital_address, nearest_hospital_phone, nearest_hospital_distance_km,
          nearest_medical_centre_name, nearest_medical_centre_address, nearest_medical_centre_phone, nearest_medical_centre_distance_km,
          mobile_coverage,
          notes, is_active, created_at, updated_at,
          asset_owner:asset_owners(owner_code, display_name)
        `)
        .eq("site_id", siteId!)
        .single();
      if (error) throw error;
      return data as unknown as SiteFull;
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["site_projects", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status, scheduled_start_date, scheduled_end_date")
        .eq("site_id", siteId!)
        .order("scheduled_start_date", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as LinkedProject[];
    },
  });

  // T-508 Phase 4 — a site page is reachable by field and supervisor, and this
  // query was pulling estimated_value for every bid on the site regardless of
  // role. Manager+ gets the value column; everyone else gets the same rows
  // without it, so the pipeline list still works on site.
  const canSeeBidValue = hasRole(user, "manager");
  const { data: bids = [] } = useQuery({
    queryKey: ["site_bids", siteId, canSeeBidValue],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bids")
        .select(canSeeBidValue
          ? "bid_id, bid_number, bid_title, status, due_date, estimated_value"
          : "bid_id, bid_number, bid_title, status, due_date")
        .eq("site_id", siteId!)
        .order("received_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as LinkedBid[];
    },
  });

  if (isLoading) {
    return <div className="p-8 flex justify-center"><Spinner /></div>;
  }

  if (error || !site) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-red-400">
            Site not found{error ? `: ${error instanceof Error ? error.message : String(error)}` : "."}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto pb-24">
      <button onClick={() => navigate("/sites")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Back to sites
      </button>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-text-muted font-mono">{site.site_code}</div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mt-0.5">
            <MapPin size={20} className="text-accent" />
            {site.site_name}
          </h1>
          <div className="mt-1.5">
            <Pill label={site.is_active ? "Active" : "Inactive"} tone={site.is_active ? "ok" : "default"} small />
          </div>
        </div>
        {canEdit && !editMode && (
          <Button icon={Pencil} variant="secondary" onClick={() => setEditMode(true)}>Edit</Button>
        )}
      </div>

      {editMode
        ? <EditForm site={site} onCancel={() => setEditMode(false)} onSaved={() => { setEditMode(false); qc.invalidateQueries({ queryKey: ["site_detail", siteId] }); qc.invalidateQueries({ queryKey: ["sites_list"] }); }} />
        : <ViewMode site={site} />
      }

      {!editMode && (
        <>
          <div className="mt-6">
            <div className="text-[13px] font-medium text-text mb-2 flex items-center gap-2">
              <FolderKanban size={15} className="text-accent" />
              Linked projects
              <span className="text-[11px] text-text-muted font-normal">({projects.length})</span>
            </div>
            <Card>
              {projects.length === 0 ? (
                <EmptyState icon={FolderKanban} title="No linked projects" description="Projects will appear here once this site is attached to one." />
              ) : (
                <div className="divide-y divide-line">
                  {projects.map(p => (
                    <Link key={p.project_id} to={`/projects/${p.project_id}`} className="block p-4 hover:bg-ink-raised/50">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[12px] text-accent">{p.project_code}</span>
                            <span className="text-[13px] text-text truncate">{p.project_name}</span>
                          </div>
                          <div className="text-[11px] text-text-muted mt-0.5">
                            {p.scheduled_start_date ? `Start ${p.scheduled_start_date}` : "No start"}
                            {p.scheduled_end_date ? ` · End ${p.scheduled_end_date}` : ""}
                          </div>
                        </div>
                        {p.status && <Pill label={p.status} tone={projectStatusTone(p.status)} small />}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="mt-6">
            <div className="text-[13px] font-medium text-text mb-2 flex items-center gap-2">
              <ClipboardList size={15} className="text-accent" />
              Linked BIDs / RFQs
              <span className="text-[11px] text-text-muted font-normal">({bids.length})</span>
            </div>
            <Card>
              {bids.length === 0 ? (
                <EmptyState icon={ClipboardList} title="No linked BIDs" description="When you create a BID against this site, it will show here." />
              ) : (
                <div className="divide-y divide-line">
                  {bids.map(b => (
                    <Link key={b.bid_id} to={`/bids/${b.bid_id}`} className="block p-4 hover:bg-ink-raised/50">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[12px] text-accent">{b.bid_number}</span>
                            <span className="text-[13px] text-text truncate">{b.bid_title}</span>
                          </div>
                          <div className="text-[11px] text-text-muted mt-0.5">
                            {b.due_date ? `Due ${b.due_date}` : "No due date"}
                            {b.estimated_value ? ` · Est $${b.estimated_value.toLocaleString()}` : ""}
                          </div>
                        </div>
                        <Pill label={b.status} tone={bidStatusTone(b.status)} small />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ViewMode — read-only rendering of all fields in grouped cards
// -----------------------------------------------------------------------------

function ViewMode({ site }: { site: SiteFull }) {
  const fullAddress = [site.address_line1, site.address_line2, site.suburb, site.state, site.postcode]
    .filter(Boolean).join(", ");

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
          <FieldRO
            label="Asset owner"
            value={site.asset_owner ? `${site.asset_owner.owner_code} · ${site.asset_owner.display_name}` : null}
          />
          <FieldRO label="Site type" value={site.site_type} />
          <FieldRO
            label="Structure height"
            value={site.structure_height_m != null
              ? `${Number(site.structure_height_m).toFixed(2)} m`
              : null}
            mono
          />
          <FieldRO label="RFNSA ID" value={site.rfnsa_id} mono />
          <FieldRO label="Carrier site ID" value={site.carrier_site_id} mono />
          <FieldRO label="what3words" value={site.what3words} mono />
          <FieldRO label="Mobile coverage" value={site.mobile_coverage} />
          <div className="col-span-2">
            <FieldRO label="Address" value={fullAddress || null} />
          </div>
          <FieldRO
            label="GPS"
            value={site.latitude != null && site.longitude != null
              ? `${site.latitude}, ${site.longitude}`
              : null}
            mono
          />
          <FieldRO label="Land owner (private)" value={site.land_owner_name} />
        </div>
      </Card>

      {(site.access_details || site.access_restrictions || site.parking_notes || site.key_location) && (
        <Card>
          <div className="p-5 space-y-3 text-[13px]">
            <div className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Operational</div>
            <FieldRO label="Access details" value={site.access_details} block />
            <FieldRO label="Access restrictions" value={site.access_restrictions} block />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <FieldRO label="Parking" value={site.parking_notes} />
              <FieldRO label="Key location" value={site.key_location} />
            </div>
          </div>
        </Card>
      )}

      {(site.hazards_summary || site.nearest_hospital_name || site.nearest_medical_centre_name) && (
        <Card>
          <div className="p-5 space-y-3 text-[13px]">
            <div className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Emergency &amp; safety</div>
            {isRemoteKm(site.nearest_hospital_distance_km) && (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12px] text-danger"><b>Remote site</b> — nearest hospital is {site.nearest_hospital_distance_km} km away. Every job here gets the remote-site warning in its Site folder.</div>
            )}
            <FieldRO label="Hazards summary" value={site.hazards_summary} block />
            <div className="grid grid-cols-3 gap-x-6 gap-y-3">
              <FieldRO label="Nearest hospital (emergency)" value={site.nearest_hospital_name} />
              <FieldRO label="Distance" value={site.nearest_hospital_distance_km != null ? `${site.nearest_hospital_distance_km} km` : null} />
              <FieldRO label="Phone" value={site.nearest_hospital_phone} mono />
            </div>
            <FieldRO label="Hospital address" value={site.nearest_hospital_address} block />
            {site.nearest_medical_centre_name && (<>
              <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                <FieldRO label="Doctor / medical centre (minor first aid)" value={site.nearest_medical_centre_name} />
                <FieldRO label="Distance" value={site.nearest_medical_centre_distance_km != null ? `${site.nearest_medical_centre_distance_km} km` : null} />
                <FieldRO label="Phone" value={site.nearest_medical_centre_phone} mono />
              </div>
              <FieldRO label="Medical centre address" value={site.nearest_medical_centre_address} block />
            </>)}
          </div>
        </Card>
      )}

      {site.notes && (
        <Card>
          <div className="p-5 text-[13px]">
            <div className="text-[12px] font-medium text-text-muted uppercase tracking-wide mb-2">Notes</div>
            <div className="text-text whitespace-pre-wrap">{site.notes}</div>
          </div>
        </Card>
      )}
    </div>
  );
}

function FieldRO({ label, value, mono = false, block = false }: { label: string; value: string | null; mono?: boolean; block?: boolean }) {
  return (
    <div className={block ? "" : ""}>
      <div className="text-[11px] uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`text-text ${mono ? "font-mono text-[12px]" : ""} ${block ? "whitespace-pre-wrap" : ""}`}>
        {value ?? <span className="text-text-dim">—</span>}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EditForm — full edit with same field groupings as NewSiteScreen
// -----------------------------------------------------------------------------

function EditForm({ site, onCancel, onSaved }: { site: SiteFull; onCancel: () => void; onSaved: () => void }) {
  const [f, setF] = useState(site);
  const [med, setMed] = useState<MedicalValues>(medicalFromSite(site));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setF(site); setMed(medicalFromSite(site)); }, [site]);

  const set = <K extends keyof SiteFull>(k: K, v: SiteFull[K]) => setF(prev => ({ ...prev, [k]: v }));

  const { data: assetOwners = [] } = useQuery({
    queryKey: ["asset_owners_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_owners")
        .select("asset_owner_id, owner_code, display_name")
        .eq("is_active", true)
        .order("display_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!f.site_name.trim()) throw new Error("Site name is required.");
      const payload = {
        site_name: f.site_name.trim(),
        rfnsa_id: f.rfnsa_id?.trim() || null,
        carrier_site_id: f.carrier_site_id?.trim() || null,
        site_type: f.site_type || null,
        structure_height_m: f.structure_height_m != null && String(f.structure_height_m).trim() !== ""
          ? parseFloat(String(f.structure_height_m))
          : null,
        asset_owner_id: f.asset_owner_id || null,
        address_line1: f.address_line1?.trim() || null,
        address_line2: f.address_line2?.trim() || null,
        suburb: f.suburb?.trim() || null,
        state: f.state?.trim() || null,
        postcode: f.postcode?.trim() || null,
        latitude:  f.latitude  != null && String(f.latitude).trim()  !== "" ? parseFloat(String(f.latitude))  : null,
        longitude: f.longitude != null && String(f.longitude).trim() !== "" ? parseFloat(String(f.longitude)) : null,
        what3words: f.what3words?.trim() || null,
        land_owner_name: f.land_owner_name?.trim() || null,
        access_details: f.access_details?.trim() || null,
        access_restrictions: f.access_restrictions?.trim() || null,
        parking_notes: f.parking_notes?.trim() || null,
        key_location: f.key_location?.trim() || null,
        hazards_summary: f.hazards_summary?.trim() || null,
        ...medicalToPayload(med),
        mobile_coverage: f.mobile_coverage || null,
        notes: f.notes?.trim() || null,
        is_active: f.is_active,
      };
      const { error } = await (supabase as any).from("sites").update(payload).eq("site_id", f.site_id);
      if (error) throw error;
    },
    onSuccess: () => onSaved(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Save failed"),
  });

  return (
    <Card>
      <div className="p-5 border-b border-line">
        <div className="text-[13px] font-medium text-text mb-3">Identity</div>
        <div className="space-y-3">
          <Field label="Site name *">
            <Input value={f.site_name} onChange={e => set("site_name", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="RFNSA ID">
              <Input type="number" mono value={f.rfnsa_id ?? ""} onChange={e => set("rfnsa_id", e.target.value)} />
            </Field>
            <Field label="Carrier site ID">
              <Input mono value={f.carrier_site_id ?? ""} onChange={e => set("carrier_site_id", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Site type">
              <Select value={f.site_type ?? ""} onChange={e => set("site_type", e.target.value || null)}>
                <option value="">— none —</option>
                {SITE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Structure height (m)" hint="Top of structure.">
              <Input
                type="number"
                step="0.01"
                mono
                value={f.structure_height_m == null ? "" : String(f.structure_height_m)}
                onChange={e => set("structure_height_m", e.target.value === "" ? null : (e.target.value as any))}
                placeholder="e.g. 30.00"
              />
            </Field>
            <Field label="Asset owner">
              <Select value={f.asset_owner_id ?? ""} onChange={e => set("asset_owner_id", e.target.value || null)}>
                <option value="">— none —</option>
                {assetOwners.map(ao => (
                  <option key={ao.asset_owner_id} value={ao.asset_owner_id}>
                    {ao.owner_code} · {ao.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <input type="checkbox" checked={f.is_active} onChange={e => set("is_active", e.target.checked)} className="accent-accent" />
            <span className="text-[13px] text-text">Active</span>
          </label>
        </div>
      </div>

      <div className="p-5 border-b border-line">
        <div className="text-[13px] font-medium text-text mb-3">Location</div>
        <div className="space-y-3">
          <Field label="Address line 1">
            <Input value={f.address_line1 ?? ""} onChange={e => set("address_line1", e.target.value)} />
          </Field>
          <Field label="Address line 2">
            <Input value={f.address_line2 ?? ""} onChange={e => set("address_line2", e.target.value)} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Suburb">
              <Input value={f.suburb ?? ""} onChange={e => set("suburb", e.target.value)} />
            </Field>
            <Field label="State">
              <Select value={f.state ?? ""} onChange={e => set("state", e.target.value || null)}>
                <option value="">—</option>
                {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="Postcode">
              <Input mono value={f.postcode ?? ""} onChange={e => set("postcode", e.target.value)} maxLength={4} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude" hint="Decimal degrees, e.g. -33.8688. Used for the front map.">
              <Input mono value={f.latitude == null ? "" : String(f.latitude)} onChange={e => set("latitude", e.target.value as any)} placeholder="-33.8688" />
            </Field>
            <Field label="Longitude" hint="Decimal degrees, e.g. 151.2093.">
              <Input mono value={f.longitude == null ? "" : String(f.longitude)} onChange={e => set("longitude", e.target.value as any)} placeholder="151.2093" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="what3words">
              <Input mono value={f.what3words ?? ""} onChange={e => set("what3words", e.target.value)} />
            </Field>
            <Field label="Land owner name">
              <Input value={f.land_owner_name ?? ""} onChange={e => set("land_owner_name", e.target.value)} />
            </Field>
          </div>
        </div>
      </div>

      <div className="p-5 border-b border-line">
        <div className="text-[13px] font-medium text-text mb-3">Operational</div>
        <div className="space-y-3">
          <Field label="Access details">
            <Textarea rows={2} value={f.access_details ?? ""} onChange={e => set("access_details", e.target.value)} />
          </Field>
          <Field label="Access restrictions">
            <Textarea rows={2} value={f.access_restrictions ?? ""} onChange={e => set("access_restrictions", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Parking notes">
              <Input value={f.parking_notes ?? ""} onChange={e => set("parking_notes", e.target.value)} />
            </Field>
            <Field label="Key location">
              <Input value={f.key_location ?? ""} onChange={e => set("key_location", e.target.value)} />
            </Field>
          </div>
        </div>
      </div>

      <div className="p-5 border-b border-line">
        <div className="text-[13px] font-medium text-text mb-3">Emergency &amp; safety</div>
        <div className="space-y-3">
          <Field label="Hazards summary">
            <Textarea rows={2} value={f.hazards_summary ?? ""} onChange={e => set("hazards_summary", e.target.value)} />
          </Field>
          <MedicalLookup lat={f.latitude} lng={f.longitude} address={[f.address_line1, f.suburb, f.state, f.postcode].filter(Boolean).join(", ")} value={med} onChange={setMed}/>
          <Field label="Mobile coverage">
            <Select value={f.mobile_coverage ?? ""} onChange={e => set("mobile_coverage", e.target.value || null)}>
              <option value="">— unknown —</option>
              {MOBILE_COVERAGE.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </div>
      </div>

      <div className="p-5">
        <Field label="Notes">
          <Textarea rows={3} value={f.notes ?? ""} onChange={e => set("notes", e.target.value)} />
        </Field>
      </div>

      {err && (
        <div className="px-5 py-2 bg-red-900/20 border-t border-red-900/40 text-[12px] text-red-300">{err}</div>
      )}

      <div className="p-4 border-t border-line flex justify-end gap-2">
        <Button variant="secondary" icon={X} onClick={onCancel} type="button">Cancel</Button>
        <Button icon={CheckCircle2} onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !f.site_name.trim()} type="button">
          {saveMut.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}
